import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { ApiError, type HorecaPromotionDto } from "@finora/api-client";
import { Button, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";

const inputCls = "h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none";
const DAYS = ["Pon", "Uto", "Sre", "Čet", "Pet", "Sub", "Ned"];
const money = (n: number) => n.toLocaleString("sr-RS", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function errMsg(e: unknown) {
  if (e instanceof ApiError) {
    const m = e.response.message;
    return Array.isArray(m) ? m.join(" ") : m;
  }
  return "Radnja nije uspela.";
}

type Draft = {
  id?: string;
  kind: "HAPPY_HOUR" | "COMBO";
  name: string;
  days: number[];
  allDay: boolean;
  time_from: string;
  time_to: string;
  valid_from: string;
  valid_to: string;
  discount_type: "PERCENT" | "FIXED_PRICE";
  discount_value: string;
  product_ids: string[];
  category_ids: string[];
  combo_items: { product_id: string; quantity: number }[];
  combo_price: string;
};

const emptyDraft = (kind: Draft["kind"]): Draft => ({
  kind, name: "", days: [], allDay: kind === "COMBO", time_from: "17:00", time_to: "19:00", valid_from: "", valid_to: "",
  discount_type: "PERCENT", discount_value: "", product_ids: [], category_ids: [], combo_items: [], combo_price: "",
});

const fromDto = (p: HorecaPromotionDto): Draft => ({
  id: p.id, kind: p.kind, name: p.name, days: p.days, allDay: !p.time_from, time_from: p.time_from ?? "17:00", time_to: p.time_to ?? "19:00",
  valid_from: p.valid_from ?? "", valid_to: p.valid_to ?? "", discount_type: p.discount_type ?? "PERCENT", discount_value: p.discount_value === null ? "" : String(p.discount_value),
  product_ids: p.product_ids, category_ids: p.category_ids, combo_items: p.combo_items.map((c) => ({ product_id: c.product_id, quantity: c.quantity })), combo_price: p.combo_price === null ? "" : String(p.combo_price),
});

export function PromocijeTab() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const list = useQuery({ queryKey: ["horeca-promos"], queryFn: () => api.listHorecaPromotions(), retry: false });
  const cats = useQuery({ queryKey: ["horeca-categories"], queryFn: () => api.listProductCategories(), retry: false });
  const products = useQuery({ queryKey: ["horeca-promo-products", search], queryFn: () => api.listProducts({ search }), enabled: search.trim().length >= 2, retry: false });
  const names = useQuery({ queryKey: ["horeca-promo-names"], queryFn: () => api.listProducts(), retry: false });
  const nameOf = (id: string) => names.data?.find((p) => p.id === id)?.name ?? "artikal";
  const done = () => { setDraft(null); setErr(null); qc.invalidateQueries({ queryKey: ["horeca-promos"] }); };
  const save = useMutation({
    mutationFn: (d: Draft) => {
      const body = {
        kind: d.kind, name: d.name, days: d.days,
        time_from: d.allDay ? null : d.time_from, time_to: d.allDay ? null : d.time_to,
        valid_from: d.valid_from || null, valid_to: d.valid_to || null,
        ...(d.kind === "HAPPY_HOUR"
          ? { discount_type: d.discount_type, discount_value: Number(d.discount_value.replace(",", ".")), product_ids: d.product_ids, category_ids: d.category_ids }
          : { combo_items: d.combo_items.map((c) => ({ product_id: c.product_id, variation_id: null, quantity: c.quantity })), combo_price: Number(d.combo_price.replace(",", ".")) }),
      };
      return d.id ? api.updateHorecaPromotion(d.id, body) : api.createHorecaPromotion(body);
    },
    onSuccess: done,
    onError: (e) => setErr(errMsg(e)),
  });
  const toggle = useMutation({ mutationFn: (p: HorecaPromotionDto) => api.updateHorecaPromotion(p.id, { is_active: !p.is_active }), onSuccess: done, onError: (e) => setErr(errMsg(e)) });
  const del = useMutation({ mutationFn: (id: string) => api.deleteHorecaPromotion(id), onSuccess: done, onError: (e) => setErr(errMsg(e)) });

  const set = (patch: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...patch } : d));
  const addCombo = (id: string) => draft && set({ combo_items: draft.combo_items.some((c) => c.product_id === id) ? draft.combo_items.map((c) => (c.product_id === id ? { ...c, quantity: c.quantity + 1 } : c)) : [...draft.combo_items, { product_id: id, quantity: 1 }] });

  const describe = (p: HorecaPromotionDto) => {
    const when = [p.days.length ? p.days.map((d) => DAYS[d - 1]).join(", ") : "svaki dan", p.time_from ? `${p.time_from}–${p.time_to}` : "ceo dan"].join(" · ");
    const what = p.kind === "COMBO"
      ? `${p.combo_items.map((c) => `${c.quantity}× ${nameOf(c.product_id)}`).join(" + ")} = ${money(p.combo_price ?? 0)}`
      : `${p.discount_type === "PERCENT" ? `−${p.discount_value}%` : `cena ${money(p.discount_value ?? 0)}`} · ${[...p.product_ids.map(nameOf), ...p.category_ids.map((id) => cats.data?.find((c) => c.id === id)?.name ?? "kategorija")].join(", ")}`;
    return `${what} — ${when}`;
  };

  if (list.isLoading) return <Skeleton className="h-20 w-full" />;
  const rows = list.data?.promotions ?? [];

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-500">
        Happy Hour: popust u procentima ili fiksna cena u određeno vreme. Kombo: više artikala za jednu cenu (npr. kafa + sok = 220), važi uvek ili u izabrano vreme. Kasa i telefon konobara dobijaju promocije sa servera.
      </p>
      {err && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}
      {!draft && (
        <div className="flex gap-2">
          <Button onClick={() => setDraft(emptyDraft("HAPPY_HOUR"))}><Plus className="mr-1 h-4 w-4" aria-hidden /> Happy Hour</Button>
          <Button onClick={() => setDraft(emptyDraft("COMBO"))}><Plus className="mr-1 h-4 w-4" aria-hidden /> Kombo</Button>
        </div>
      )}

      {draft && (
        <div className="space-y-3 rounded-xl border border-border bg-surface p-3 shadow-subtle">
          <div className="text-sm font-semibold">{draft.id ? "Izmena" : "Nova"} promocija — {draft.kind === "COMBO" ? "Kombo" : "Happy Hour"}</div>
          <label className="block text-xs text-slate-500">Naziv<input className={inputCls} value={draft.name} onChange={(e) => set({ name: e.target.value })} placeholder={draft.kind === "COMBO" ? "npr. Kafa + sok" : "npr. Happy Hour piće"} /></label>

          {draft.kind === "HAPPY_HOUR" ? (
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-xs text-slate-500">Vrsta
                <select className={inputCls + " block w-56"} value={draft.discount_type} onChange={(e) => set({ discount_type: e.target.value as Draft["discount_type"] })}>
                  <option value="PERCENT">Popust u procentima</option>
                  <option value="FIXED_PRICE">Fiksna cena</option>
                </select>
              </label>
              <label className="text-xs text-slate-500">{draft.discount_type === "PERCENT" ? "Popust (%)" : "Nova cena (din)"}
                <input className={inputCls + " block w-32"} inputMode="decimal" value={draft.discount_value} onChange={(e) => set({ discount_value: e.target.value })} />
              </label>
            </div>
          ) : (
            <label className="block text-xs text-slate-500">Cena komba (din)
              <input className={inputCls + " block w-40"} inputMode="decimal" value={draft.combo_price} onChange={(e) => set({ combo_price: e.target.value })} />
            </label>
          )}

          <div className="space-y-1">
            <label className="block text-xs text-slate-500">{draft.kind === "COMBO" ? "Artikli u kombu" : "Artikli"} — pretraga (bar 2 slova)
              <input className={inputCls} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="npr. kafa" />
            </label>
            {(products.data ?? []).slice(0, 8).map((p) => (
              <button key={p.id} className="mr-1 rounded-full border border-border px-3 py-1 text-xs hover:bg-slate-50"
                onClick={() => (draft.kind === "COMBO" ? addCombo(p.id) : !draft.product_ids.includes(p.id) && set({ product_ids: [...draft.product_ids, p.id] }))}>
                + {p.name}
              </button>
            ))}
            {draft.kind === "COMBO" ? (
              <div className="flex flex-wrap gap-2 pt-1">
                {draft.combo_items.map((c) => (
                  <span key={c.product_id} className="flex items-center gap-1 rounded-lg bg-slate-100 px-2 py-1 text-sm">
                    <input type="number" min={1} className="h-7 w-12 rounded border border-border px-1 text-sm" value={c.quantity}
                      onChange={(e) => set({ combo_items: draft.combo_items.map((x) => (x.product_id === c.product_id ? { ...x, quantity: Math.max(1, Number(e.target.value) || 1) } : x)) })} />
                    × {nameOf(c.product_id)}
                    <button aria-label="Ukloni" onClick={() => set({ combo_items: draft.combo_items.filter((x) => x.product_id !== c.product_id) })}>×</button>
                  </span>
                ))}
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-2 pt-1">
                  {draft.product_ids.map((id) => (
                    <span key={id} className="flex items-center gap-1 rounded-lg bg-slate-100 px-2 py-1 text-sm">{nameOf(id)}
                      <button aria-label="Ukloni" onClick={() => set({ product_ids: draft.product_ids.filter((x) => x !== id) })}>×</button>
                    </span>
                  ))}
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1">
                  {(cats.data ?? []).map((c) => (
                    <label key={c.id} className="flex items-center gap-1 text-sm">
                      <input type="checkbox" checked={draft.category_ids.includes(c.id)} onChange={(e) => set({ category_ids: e.target.checked ? [...draft.category_ids, c.id] : draft.category_ids.filter((x) => x !== c.id) })} />
                      cela kategorija: {c.name}
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-xs text-slate-500">Dani (prazno = svaki dan):</span>
              {DAYS.map((d, i) => (
                <label key={d} className="flex items-center gap-1">
                  <input type="checkbox" checked={draft.days.includes(i + 1)} onChange={(e) => set({ days: e.target.checked ? [...draft.days, i + 1].sort() : draft.days.filter((x) => x !== i + 1) })} />{d}
                </label>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <label className="flex items-center gap-1"><input type="checkbox" checked={draft.allDay} onChange={(e) => set({ allDay: e.target.checked })} /> Ceo dan</label>
              {!draft.allDay && (
                <>
                  <label className="text-xs text-slate-500">Od<input type="time" className={inputCls + " block w-32"} value={draft.time_from} onChange={(e) => set({ time_from: e.target.value })} /></label>
                  <label className="text-xs text-slate-500">Do<input type="time" className={inputCls + " block w-32"} value={draft.time_to} onChange={(e) => set({ time_to: e.target.value })} /></label>
                  <span className="text-xs text-slate-400">Može i preko ponoći (22:00–02:00).</span>
                </>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-xs text-slate-500">Važi od (neobavezno)<input type="date" className={inputCls + " block w-44"} value={draft.valid_from} onChange={(e) => set({ valid_from: e.target.value })} /></label>
              <label className="text-xs text-slate-500">Važi do<input type="date" className={inputCls + " block w-44"} value={draft.valid_to} onChange={(e) => set({ valid_to: e.target.value })} /></label>
            </div>
          </div>

          <div className="flex gap-2">
            <Button disabled={save.isPending || !draft.name.trim()} onClick={() => save.mutate(draft)}>Sačuvaj</Button>
            <button className="rounded-lg px-3 py-2 text-sm hover:bg-slate-100" onClick={() => { setDraft(null); setErr(null); }}>Otkaži</button>
          </div>
        </div>
      )}

      {rows.length === 0 && !draft && <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-500">Još nema promocija.</p>}
      {rows.map((p) => (
        <div key={p.id} className={"flex items-start justify-between gap-2 rounded-xl border border-border bg-surface p-3 shadow-subtle " + (p.is_active ? "" : "opacity-60")}>
          <div className="min-w-0">
            <div className="text-sm font-semibold">{p.name} <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-normal">{p.kind === "COMBO" ? "Kombo" : "Happy Hour"}</span></div>
            <div className="text-xs text-slate-600">{describe(p)}</div>
          </div>
          <div className="flex shrink-0 items-center gap-1 text-xs">
            <button className="rounded px-2 py-1 hover:bg-slate-100" onClick={() => setDraft(fromDto(p))}>Izmeni</button>
            <button className="rounded px-2 py-1 hover:bg-slate-100" onClick={() => toggle.mutate(p)}>{p.is_active ? "Isključi" : "Uključi"}</button>
            <button className="rounded p-1.5 text-red-600 hover:bg-red-50" aria-label="Obriši" onClick={() => window.confirm(`Obrisati promociju „${p.name}“?`) && del.mutate(p.id)}><Trash2 className="h-4 w-4" aria-hidden /></button>
          </div>
        </div>
      ))}
    </div>
  );
}
